import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"

const API_URL = process.env.REACT_APP_API_URL

// Fetch all results (admin sees all, students see their own)
export const fetchResults = createAsyncThunk("results/fetchResults", async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.get(`${API_URL}/results`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return response.data.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

// Create a new result
export const createResult = createAsyncThunk("results/createResult", async (resultData, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.post(`${API_URL}/results`, resultData, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return response.data.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

// Update an existing result
export const updateResult = createAsyncThunk("results/updateResult", async ({ id, data }, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.put(`${API_URL}/results/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return response.data.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

// Delete a result
export const deleteResult = createAsyncThunk("results/deleteResult", async (id, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    await axios.delete(`${API_URL}/results/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return id
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

const resultSlice = createSlice({
  name: "results",
  initialState: {
    results: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Results
      .addCase(fetchResults.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchResults.fulfilled, (state, action) => {
        state.loading = false
        state.results = action.payload
      })
      .addCase(fetchResults.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Create Result
      .addCase(createResult.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createResult.fulfilled, (state) => {
        state.loading = false
        // Rely on fetchResults in component to refresh state
      })
      .addCase(createResult.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Update Result
      .addCase(updateResult.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateResult.fulfilled, (state, action) => {
        state.loading = false
        const index = state.results.findIndex((r) => r.result_id === action.payload.result_id)
        if (index !== -1) {
          state.results[index] = action.payload
        }
      })
      .addCase(updateResult.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Delete Result
      .addCase(deleteResult.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteResult.fulfilled, (state, action) => {
        state.loading = false
        state.results = state.results.filter((r) => r.result_id !== action.payload)
      })
      .addCase(deleteResult.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { clearError } = resultSlice.actions
export default resultSlice.reducer
